import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Badge,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  Favorite as FavoriteIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { SortOption, ViewMode } from './types';

interface SearchAndFilterProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  favoritesCount: number;
}

const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  searchQuery,
  onSearchChange,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  favoritesCount
}) => {
  return (
    <Paper sx={{ p: 3, mb: 4, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, 
        gap: 3, 
        alignItems: 'center' 
      }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search products, categories, features..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#1a237e' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              '&:hover fieldset': {
                borderColor: '#1a237e',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1a237e',
              },
            },
          }}
        />

        {/* Sort By */}
        <FormControl fullWidth>
          <InputLabel sx={{ color: '#1a237e' }}>Sort By</InputLabel>
          <Select
            value={sortBy}
            label="Sort By"
            onChange={(e) => onSortChange(e.target.value as SortOption)}
            sx={{ borderRadius: '12px' }}
          >
            <MenuItem value="popularity">Most Popular</MenuItem>
            <MenuItem value="rating">Highest Rated</MenuItem>
            <MenuItem value="newest">Newest First</MenuItem>
          </Select>
        </FormControl>

        {/* View Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Grid View">
              <IconButton
                onClick={() => onViewModeChange('grid')}
                sx={{ 
                  color: viewMode === 'grid' ? '#1a237e' : '#666',
                  backgroundColor: viewMode === 'grid' ? 'rgba(26, 35, 126, 0.1)' : 'transparent'
                }}
              >
                <ViewModuleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="List View">
              <IconButton
                onClick={() => onViewModeChange('list')}
                sx={{ 
                  color: viewMode === 'list' ? '#1a237e' : '#666',
                  backgroundColor: viewMode === 'list' ? 'rgba(26, 35, 126, 0.1)' : 'transparent'
                }}
              >
                <ViewListIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Badge badgeContent={favoritesCount} color="error">
            <Tooltip title="Favorites">
              <IconButton sx={{ color: '#e91e63' }}>
                <FavoriteIcon />
              </IconButton>
            </Tooltip>
          </Badge>
        </Box>
      </Box>
    </Paper>
  );
};

export default SearchAndFilter;
